// Сигнал «переход в отдел инициирован кликом по меню» → открыть дашборд отдела,
// а не восстановленную из localStorage вкладку. Reload (F5) флаг не ставит,
// поэтому при перезагрузке срабатывает обычное восстановление.

const OPEN_DEPT_DASHBOARD_KEY = "portal:open-dept-dashboard";

export function markOpenDeptDashboard(deptKey: string) {
  try {
    window.sessionStorage.setItem(
      OPEN_DEPT_DASHBOARD_KEY,
      JSON.stringify({ key: deptKey, ts: Date.now() }),
    );
  } catch {}
}

/** Считать и сбросить флаг; true — если открыть дашборд этого отдела.
 *  Свежесть 3с отсекает «залипший» флаг (клик по уже открытому отделу без перехода). */
export function consumeOpenDeptDashboard(deptKey: string): boolean {
  try {
    const raw = window.sessionStorage.getItem(OPEN_DEPT_DASHBOARD_KEY);
    if (!raw) return false;
    window.sessionStorage.removeItem(OPEN_DEPT_DASHBOARD_KEY);
    const { key, ts } = JSON.parse(raw) as { key: string; ts: number };
    return key === deptKey && Date.now() - ts < 3000;
  } catch {
    return false;
  }
}


// ── Прямой переход в модуль отдела из сайдбара ──────────────────────────────
// Навигация переезжает в левый сайдбар: отделы списком, модули раскрытием под
// ними (структура референса PLUX). Роутинг остаётся прежним — /departments/<key>,
// а какой модуль открыть, страница отдела читает из localStorage. Поэтому
// сайдбару достаточно записать туда ключ ПЕРЕД переходом.
export const activeModuleStorageKey = (deptKey: string) => `company-portal:active-module:${deptKey}`;

// ⚠️ ПОДРАЗДЕЛ ПОМНИТСЯ ПО РАЗДЕЛУ, а не по отделу. Ключ был один на отдел, и
// подраздел одного раздела подставлялся в другой: ушёл из «Ассортимента» в
// «Закуп/Доставку» — сохранённый «Просчёт заказа» уже не про неё, а вернувшись
// в «Ассортимент», человек не находил там того, что оставил.
export const activeSubStorageKey = (deptKey: string, sectionKey: string) =>
  `company-portal:active-sub:${deptKey}:${sectionKey}`;

/** Ключ прежней схемы (один подраздел на отдел) — читаем при переезде и убираем. */
const legacySubStorageKey = (deptKey: string) => `company-portal:active-sub:${deptKey}`;

/** Подраздел, запомненный для этого раздела. Проверку «есть ли он в разделе»
 *  делает страница отдела (resolveSubKey): здесь про права ничего не известно. */
export function readActiveSub(deptKey: string, sectionKey: string | null | undefined): string | undefined {
  if (!sectionKey) return undefined;
  try {
    return window.localStorage.getItem(activeSubStorageKey(deptKey, sectionKey)) ?? undefined;
  } catch {
    return undefined;
  }
}

export function writeActiveSub(deptKey: string, sectionKey: string, subKey: string) {
  try {
    window.localStorage.setItem(activeSubStorageKey(deptKey, sectionKey), subKey);
  } catch {}
}

/** Переезд с прежней схемы (один подраздел на отдел) на ключ раздела.
 *  Значение отдаём тому разделу, который был открыт последним, — другого
 *  владельца у него нет. Не подошло разделу — страница отдела всё равно
 *  откатится на первый видимый подраздел (resolveSubKey). Вызывать один раз при
 *  заходе в отдел: после переноса старый ключ убираем, чтобы не остался мусором. */
export function migrateLegacyActiveSub(deptKey: string, sectionKey: string | null | undefined) {
  try {
    const legacy = window.localStorage.getItem(legacySubStorageKey(deptKey));
    if (legacy === null) return;
    window.localStorage.removeItem(legacySubStorageKey(deptKey));
    if (!sectionKey) return;
    const key = activeSubStorageKey(deptKey, sectionKey);
    if (window.localStorage.getItem(key) === null) window.localStorage.setItem(key, legacy);
  } catch {}
}

export const OPEN_MODULE_EVENT = "portal:open-module";

export function markOpenDeptModule(deptKey: string, moduleKey: string, subKey?: string) {
  try {
    window.localStorage.setItem(activeModuleStorageKey(deptKey), moduleKey);
    // Под-раздел пишем тем же ключом, что читает страница отдела при заходе:
    // клик по вложенному пункту сайдбара должен открывать именно его.
    // ⚠️ БЕЗ subKey НИЧЕГО НЕ СТИРАЕМ. Раньше здесь стоял removeItem, и клик по
    // разделу в меню выбрасывал сохранённый подраздел: страница получала
    // `initialTab=undefined`, модуль подставлял свой внутренний дефолт, а полоса
    // подразделов подсвечивала первый пункт — отмеченное и открытое расходились
    // (Ассортимент: подсвечен «Ассортимент», открыт «Дашборд»). Какой подраздел
    // открыть, решает страница отдела: она одна знает список видимых подразделов.
    if (subKey) writeActiveSub(deptKey, moduleKey, subKey);
    // Флаг «открыть дашборд» сбрасываем: он сильнее и перебил бы выбранный модуль.
    window.sessionStorage.removeItem(OPEN_DEPT_DASHBOARD_KEY);
    // ⚠️ Событие обязательно. localStorage страница отдела читает только при СМЕНЕ
    // отдела; если пользователь уже в этом отделе и выбирает другой модуль в
    // сайдбаре, роут не меняется и перечитывания не происходит. Событие говорит
    // открытой странице переключиться немедленно.
    window.dispatchEvent(new CustomEvent(OPEN_MODULE_EVENT, { detail: { deptKey, moduleKey, subKey } }));
  } catch {}
}

// ── Какой раздел отдела открыт сейчас ───────────────────────────────────────
// Это состояние, а не данные: СПИСОК разделов сайдбар считает сам
// (lib/dept-sections), а вот что именно из него открыто, знает только страница
// отдела — она разрешает сохранённый ключ, откатывается на первый доступный
// раздел и переключает подразделы изнутри. Поэтому страница объявляет активный
// раздел, а сайдбар слушает; localStorage для этого не годится — события
// `storage` в своей же вкладке не приходят.
export type ActiveSection = { deptKey: string; moduleKey: string | null; subKey?: string };

let activeSection: ActiveSection = { deptKey: "", moduleKey: null };
const sectionListeners = new Set<(s: ActiveSection) => void>();

export function publishActiveSection(deptKey: string, moduleKey: string | null, subKey?: string) {
  if (
    activeSection.deptKey === deptKey
    && activeSection.moduleKey === moduleKey
    && activeSection.subKey === subKey
  ) return;
  activeSection = { deptKey, moduleKey, subKey };
  // ⚠️ Уведомляем НЕ синхронно: объявление происходит в рендере страницы, а
  // подписчик (оболочка) стоит выше в дереве — синхронный setState оттуда даёт
  // «Cannot update a component while rendering a different component».
  const snap = activeSection;
  queueMicrotask(() => { if (activeSection === snap) sectionListeners.forEach((l) => l(snap)); });
}

export function subscribeActiveSection(l: (s: ActiveSection) => void) {
  sectionListeners.add(l);
  l(activeSection);
  return () => { sectionListeners.delete(l); };
}
